import { formatearMoneda, type ArqueoInput, type Conteo, type Moneda } from '@barbones/shared';
import { CircleAlert, CircleCheck, TrendingUp } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Pestana, PestanaContenido, Pestanas, PestanasLista } from '@/components/ui/data';
import { Campo, Input } from '@/components/ui/form';
import { cn } from '@/lib/utils';

interface Props {
  denominacionesMXN: number[];
  denominacionesUSD: number[];
  esperadoMXN: number;
  esperadoUSD: number;
  onConfirmar: (arqueo: ArqueoInput) => void;
  textoConfirmar: string;
  cargando?: boolean;
  /** Campos extra (retiro en el corte). */
  extra?: ReactNode;
  /** Arqueo con el que arranca el formulario (al volver desde el paso del fondo del cierre). */
  inicial?: ArqueoInput;
}

const aPiezas = (c: Conteo = {}) => Object.fromEntries(Object.entries(c).map(([d, n]) => [d, String(n)]));

function limpiar(piezas: Record<string, string>): Conteo {
  return Object.fromEntries(
    Object.entries(piezas)
      .map(([d, v]) => [d, Number.parseInt(v, 10)] as const)
      .filter(([, v]) => Number.isFinite(v) && v > 0),
  );
}

const suma = (c: Conteo) => Object.entries(c).reduce((s, [d, n]) => s + Number(d) * n, 0);

/** Lo contado en un arqueo, en centavos por moneda (sin validar denominaciones: eso lo hace la API). */
export function contadoDe(a: ArqueoInput): { MXN: number; USD: number } {
  return a.modo === 'total' ? { MXN: a.totalMXN, USD: a.totalUSD } : { MXN: suma(a.conteoMXN), USD: suma(a.conteoUSD) };
}

/** Texto en pesos → centavos; `null` si está vacío, no es número o es negativo. */
function aCentavos(texto: string): number | null {
  if (texto.trim() === '') return null;
  const n = Number(texto);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
}

const aTexto = (centavos: number | undefined) => (centavos === undefined ? '' : String(centavos / 100));

function Grilla({ moneda, denominaciones, piezas, onCambiar }: { moneda: Moneda; denominaciones: number[]; piezas: Record<string, string>; onCambiar: (d: string, v: string) => void }) {
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-3">
      {denominaciones.map((d) => {
        const etiqueta = formatearMoneda(d, moneda);
        const n = Number.parseInt(piezas[d] ?? '', 10) || 0;
        return (
          <label key={d} className="flex items-center gap-2 rounded-md border border-border bg-surface px-2 py-1.5 focus-within:border-accent">
            <span className="cifras w-20 shrink-0 text-[13px] font-medium">{etiqueta}</span>
            <span className="text-foreground-muted" aria-hidden>
              ×
            </span>
            <input
              type="number"
              min={0}
              step={1}
              inputMode="numeric"
              value={piezas[d] ?? ''}
              onChange={(e) => onCambiar(String(d), e.target.value)}
              aria-label={`Piezas de ${etiqueta}`}
              className="cifras w-14 bg-transparent text-right text-[14px] outline-none"
              placeholder="0"
            />
            <span className="cifras ml-auto text-[12px] text-foreground-muted">{n ? formatearMoneda(n * d, moneda) : ''}</span>
          </label>
        );
      })}
    </div>
  );
}

/**
 * Arqueo de efectivo (ERS §5.3): por denominación o como total contado. El sistema compara
 * con lo esperado e indica cuánto falta o sobra.
 */
export function ArqueoForm({ denominacionesMXN, denominacionesUSD, esperadoMXN, esperadoUSD, onConfirmar, textoConfirmar, cargando, extra, inicial }: Props) {
  const [modo, setModo] = useState<ArqueoInput['modo']>(inicial?.modo ?? 'conteo');
  // Cada pestaña guarda su propio estado aquí: Radix desmonta el contenido de la pestaña inactiva.
  const [mxn, setMxn] = useState<Record<string, string>>(() => aPiezas(inicial?.modo === 'conteo' ? inicial.conteoMXN : undefined));
  const [usd, setUsd] = useState<Record<string, string>>(() => aPiezas(inicial?.modo === 'conteo' ? inicial.conteoUSD : undefined));
  const [totalMXN, setTotalMXN] = useState(() => aTexto(inicial?.modo === 'total' ? inicial.totalMXN : undefined));
  const [totalUSD, setTotalUSD] = useState(() => aTexto(inicial?.modo === 'total' && inicial.totalUSD > 0 ? inicial.totalUSD : undefined));
  const conteoMXN = useMemo(() => limpiar(mxn), [mxn]);
  const conteoUSD = useMemo(() => limpiar(usd), [usd]);
  const conUSD = denominacionesUSD.length > 0;
  const centavosMXN = aCentavos(totalMXN);
  const centavosUSD = conUSD && totalUSD.trim() !== '' ? aCentavos(totalUSD) : 0;
  const arqueo: ArqueoInput | null =
    modo === 'conteo'
      ? { modo, conteoMXN, conteoUSD }
      : centavosMXN !== null && centavosUSD !== null
        ? { modo, totalMXN: centavosMXN, totalUSD: centavosUSD }
        : null;
  const { MXN: contadoMXN, USD: contadoUSD } = arqueo ? contadoDe(arqueo) : { MXN: 0, USD: 0 };
  const difMXN = contadoMXN - esperadoMXN;
  const difUSD = contadoUSD - esperadoUSD;
  const faltante = difMXN < 0 || difUSD < 0;
  const sobrante = !faltante && (difMXN > 0 || difUSD > 0);

  const describir = (dif: number, moneda: Moneda) => (dif === 0 ? null : formatearMoneda(Math.abs(dif), moneda));
  const detalle = [describir(difMXN, 'MXN'), describir(difUSD, 'USD')].filter(Boolean).join(' y ');

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault();
        if (arqueo) onConfirmar(arqueo);
      }}
    >
      <Pestanas value={modo} onValueChange={(v) => setModo(v as ArqueoInput['modo'])}>
        <PestanasLista className="grid grid-cols-2">
          <Pestana value="total" className="whitespace-normal text-center">
            Cuadrar caja con efectivo total contado
          </Pestana>
          <Pestana value="conteo" className="whitespace-normal text-center">
            Cuadrar caja con conteo de billetes
          </Pestana>
        </PestanasLista>
        <PestanaContenido value="total" className="flex flex-wrap gap-4 pt-4">
          <Campo etiqueta="Efectivo total contado (MXN)" ayuda={`Esperado ${formatearMoneda(esperadoMXN)}`}>
            {(p) => <Input {...p} type="number" min="0" step="0.01" inputMode="decimal" value={totalMXN} onChange={(e) => setTotalMXN(e.target.value)} className="cifras w-48" />}
          </Campo>
          {conUSD && (
            <Campo etiqueta="Efectivo total contado (USD)" ayuda={`Esperado ${formatearMoneda(esperadoUSD, 'USD')}`}>
              {(p) => <Input {...p} type="number" min="0" step="0.01" inputMode="decimal" value={totalUSD} onChange={(e) => setTotalUSD(e.target.value)} className="cifras w-48" placeholder="0" />}
            </Campo>
          )}
        </PestanaContenido>
        <PestanaContenido value="conteo" className="flex flex-col gap-5 pt-4">
          <section>
            <div className="mb-2 flex items-baseline justify-between">
              <h3 className="text-[16px]">Pesos (MXN)</h3>
              <p className="cifras text-[13px] text-foreground-muted">
                Contado <strong data-testid="contado-MXN" className="text-foreground">{formatearMoneda(contadoMXN)}</strong> · Esperado {formatearMoneda(esperadoMXN)}
              </p>
            </div>
            <Grilla moneda="MXN" denominaciones={denominacionesMXN} piezas={mxn} onCambiar={(d, v) => setMxn((p) => ({ ...p, [d]: v }))} />
          </section>
          <section>
            <div className="mb-2 flex items-baseline justify-between">
              <h3 className="text-[16px]">Dólares (USD)</h3>
              <p className="cifras text-[13px] text-foreground-muted">
                Contado <strong data-testid="contado-USD" className="text-foreground">{formatearMoneda(contadoUSD, 'USD')}</strong> · Esperado {formatearMoneda(esperadoUSD, 'USD')}
              </p>
            </div>
            <Grilla moneda="USD" denominaciones={denominacionesUSD} piezas={usd} onCambiar={(d, v) => setUsd((p) => ({ ...p, [d]: v }))} />
          </section>
        </PestanaContenido>
      </Pestanas>

      <div
        role="status"
        aria-live="polite"
        className={cn(
          'flex items-center gap-2 rounded-lg px-4 py-3 text-[14px] font-medium',
          faltante ? 'bg-destructive-soft text-destructive' : sobrante ? 'bg-info-soft text-info' : 'bg-success-soft text-success',
        )}
      >
        {faltante ? <CircleAlert className="size-5" /> : sobrante ? <TrendingUp className="size-5" /> : <CircleCheck className="size-5" />}
        {faltante
          ? `Faltante de ${detalle}. Repónlo ahora o pide al dueño que autorice.`
          : sobrante
            ? `Sobrante de ${detalle}: se registra y se retira.`
            : 'La caja cuadra.'}
      </div>

      {extra}

      <Button type="submit" tamano="lg" cargando={cargando} disabled={!arqueo} variante={faltante ? 'peligro' : 'primario'}>
        {textoConfirmar}
      </Button>
    </form>
  );
}
